import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { environment } from '@environments/environment';
import { RequestV2Service } from '@providers/request-service-v2/request-v2.service';

export interface Material {
  id_quotation_material: number;
  name: string;
  supplier: string;
  brand: string;
  id_quotation_chapter: number;
  quotation_chapter: string;
  id_quotation: number | null;
  id_quotation_service: number;
  id_category: number | null;
  eco: boolean;
  ids_categories_children: {id: number, name: string}[];
  comment: string;
  version: any;
  versionSpecification: any[];
  quantity: number;
  quantity_customer: number;
  suggestion: {isSuggestion: boolean, matSuggested: any};
  status: string;
  date_validation: Date | null;
  images: any;
  mainImage: string | null;
  projectRooms: any[];
  alternativeMat: {
    isLoaded: boolean,
    indexSelectedFirst: number,
    indexSelectedSecond: number,
    materials: any[]
  };
}

export interface ChapterWithMaterials {
  id: number;
  title: string;
  materials: Material[];
}

@Injectable({
  providedIn: 'root'
})
export class DataMaterialsService {

  // Data Fetch
  public quotationMaterials: any = [];
  public quotationMaterialsValidation:any = [];
  public projectRooms:any = [];

  // Current Material active in materials component
  public activeMaterial:any;

  // Current Selection Material active in material component
  public activeSelectionMaterial:any;

  // Current project and current Room
  public currentProject:any;

  // Get categories ids and alternative Materials
  public idsCategories:any = [];
  public topAlternative:any = [];

  public refreshData:boolean = true;
  public units:any = [];
  public percentageValidated:number = 0;

  // Header mobile of selection Theme
  public displayHeaderMobileSelectionTheme:boolean = true;

  public chaptersWithMaterials: ChapterWithMaterials[] = [];

  private projectMaterials = { items: [] };

  constructor(
    private requestV2Service: RequestV2Service,
    private sanitization: DomSanitizer,
    private router: Router
  ) { }

  private randomIntFromInterval(min, max, mat, indexSelected) {
    if (indexSelected === 'first') {
      return Math.floor(Math.random() * (max - min + 1) + min);
    }
    if (indexSelected === 'second') {
      let index = Math.floor(Math.random() * (max - min + 1) + min);
      if (index === mat.alternativeMat.indexSelectedFirst) {
        index = this.randomIntFromInterval(min, max, mat, 'second');
      }
      return index;
    }
  }

  private getUnitType(version) {
    const unit = this.units.find(u => u.id === version.id_unit_type);
    return (unit) ? unit.type : 'NC';
  }

  private detectStatusMat(version):string {
    const isFoundMat = this.quotationMaterialsValidation
      .find(m => m.id_material_version === version.id);

    if (!isFoundMat) {
      return 'initial';
    }

    let value = 'initial';

    switch (true) {
      case  !!isFoundMat.dates.validation_lw:
        value = 'validation_lw';
        break;

      case  !!isFoundMat.dates.validation_by_signature:
        value = 'validation_by_signature';
        break;

      default:
        return value;
    }

    return value;
  }

  private detectDateValidation(version): Date | null {
    const isFoundMat = this.quotationMaterialsValidation
      .find(m => m.id_material_version === version.id);

    if (!isFoundMat) {
      return null;
    }

    if (isFoundMat.dates && isFoundMat.dates.validation_lw) {
      return new Date(isFoundMat.dates.validation_lw);
    }

    if (isFoundMat.dates && isFoundMat.dates.validation_by_signature) {
      return new Date(isFoundMat.dates.validation_by_signature);
    }

    return null;
  }

  async formatMaterials(quotMats): Promise<Material[]> {
    // Filter all quot Mats not in suggestion or in note;
    const matInitials = quotMats.filter((mat) => {
      const isSuggestion = this.quotationMaterialsValidation
        .find(quotValid =>
          quotValid.id_original_material_version === mat._related.material_versions[0].id
          && !!quotValid.id_material_version
          && !!quotValid.dates.customer_suggestion);
      return (!isSuggestion);
    });

    // concat both arrays (initial and suggested) before format them;
    const finalQuotMat = matInitials;
    return finalQuotMat.map((mat) => {
      let imagesMat = [];
      if (mat._related.material_version_images[0] !== null) {
        imagesMat = mat._related.material_version_images
          .map(img => ({
            ...img,
            url: this.getSafeStyleImgURL(img.id_image, 'small')
          })).filter(img => img.id_image !== null);
      }
      while (imagesMat.length < 3) {
        imagesMat.push({
          id_image: null,
          url: '/assets/images/placeholder-img.png'
        });
      }
      const v = { ...mat._related.material_versions[0] };
      v.unitType = this.getUnitType(mat._related.material_versions[0]);

      const quotMatComment: any = {};
      if (mat._related.quotation_material_comments
        && mat._related.quotation_material_comments.length > 0) {
        quotMatComment.content = mat._related.quotation_material_comments[0].comment;
        quotMatComment.date = mat._related.quotation_material_comments[0].date;
      }
      return {
        id_quotation_material: mat.id,
        name: mat._related.material_versions[0].name,
        supplier: (mat._related.material_suppliers.length > 0
          && mat._related.material_suppliers[0] !== null)
          ? mat._related.material_suppliers[0].name
          : 'NC',
        brand: (mat._related.material_brands.length > 0 && mat._related.material_brands[0] !== null)
          ? mat._related.material_brands[0].name
          : 'Marque Inconnue',
        id_quotation_chapter: (mat._related.quotation_chapters.length > 0)
          ? mat._related.quotation_chapters[0].id
          : null,
        quotation_chapter: (mat._related.quotation_chapters.length > 0)
          ? mat._related.quotation_chapters[0].title
          : 'Chapitre Inconnu',
        id_quotation: (mat._related.quotation_versions.length > 0)
          ? mat._related.quotation_versions[0].id_quotation
          : null,
        id_quotation_service: mat._related.quotation_services[0].id,
        id_category: (mat._related.material_product_categories.length > 0)
          ? mat._related.material_product_categories[0].id
          : null,
        eco: !!(mat._related.material_product_categories.length > 0
          && mat._related.material_product_categories.find(i => i.name === 'Eco-responsable')),
        ids_categories_children: mat._related._categories_childrens
          .map(c => ({ id: c.id, name: c.name })),
        comment: quotMatComment,
        version: v,
        versionSpecification: mat._related.material_version_specifications
          .filter(s => s !== null && s.value),
        quantity: mat.quantity,
        quantity_customer: (mat._related.quotation_service_rooms.length > 0)
          ? mat.quantity / mat._related.quotation_service_rooms.length
          : mat.quanty,
        suggestion: {
          isSuggestion: false,
          matSuggested: null
        },
        status: this.detectStatusMat(v),
        date_validation: this.detectDateValidation(v),
        images: imagesMat,
        mainImage: (mat._related.material_version_images.length > 0
          && mat._related.material_version_images[0] !== null
          && mat._related.material_version_images[0].id_image !== null)
          ? this.getSafeStyleImgURL(mat._related.material_version_images[0].id_image, 'large')
          : null,
        projectRooms: (mat._related.project_rooms.length > 0)
          ? mat._related.project_rooms.map((projectRoom) => {
            const room = this.projectRooms.find(r => r.id_room_type  === projectRoom.id_room_type);
            return { ...projectRoom, icon: room ? room.icon : null };
          })
          : [],
        alternativeMat: {
          isLoaded: false,
          indexSelectedFirst: 0,
          indexSelectedSecond: 0,
          materials: []
        }
      };
    });
  }

  public formatFullProjectRoomsWithMat() {
    const projectRooms = this.projectRooms.map((pR) => {
      const projectRoom = { ...pR };
      for (let i = 0; i < this.quotationMaterials.length ; i = i + 1) {
        const quotMat = this.quotationMaterials[i];
        const isInculeInProjectRoom = quotMat.projectRooms.find(pr => pr.id === projectRoom.id);
        if (isInculeInProjectRoom) {
          projectRoom.materials.push(quotMat);
        }
      }
      return projectRoom;
    });
    const nonAttributeRoomIndex = projectRooms.findIndex(r => r._related.room_types[0].id === 23);
    const nonAttributeMats = this.quotationMaterials.filter(qM => qM.projectRooms.length === 0);
    for (let i = 0; i < nonAttributeMats.length ; i = i + 1) {
      projectRooms[nonAttributeRoomIndex].icon = ['fal', 'tools'];
      projectRooms[nonAttributeRoomIndex].label = 'Tous matériaux';
      projectRooms[nonAttributeRoomIndex].materials.push(nonAttributeMats[i]);
    }
    return projectRooms;
  }

  public sortMatByStatus() {
    return this.projectRooms.map((pR) => {
      const matsInitial = pR.materials
        .filter(m => !['validation_lw', 'validation_by_signature'].includes(m.status));

      const matsValidated = pR.materials
        .filter(m => ['validation_lw', 'validation_by_signature'].includes(m.status));

      pR.materials = [...matsInitial, ...matsValidated];
      return pR;
    });
  }

  public getSafeStyleImgURL(id: number, size:string = 'small'): string {
    let result;
    if (typeof id !== 'undefined') {
      const sizeImg = (size === 'small') ? 'size=104x104' : 'size=380x380';
      result = this.sanitization
        .bypassSecurityTrustUrl(
          `${environment.api_url}/images/${id}?${sizeImg}`
        );
    }
    return result;
  }

  // Format project Rooms with new fields (icon, materials, etc...)
  public formatProjectRooms(projectRooms) {
    const newProjectRooms = projectRooms.map((pR) => {
      const icon = ['fal'];
      const projectRoom = { ...pR };
      icon[1] = projectRoom._related.room_types[0].icon;
      projectRoom.icon = icon;
      projectRoom.materials = [];
      projectRoom.materialsValidated = 0;
      return projectRoom;
    });

    return newProjectRooms;
  }

  public buildIdsCategories() {
    let ids = this.quotationMaterials.map(mat => mat.id_category).filter(c => c);
    ids = [...new Set(ids)];
    return ids;
  }

  public async fetchTopAlternatives() {
    return await Promise.all(this.idsCategories.map(async (id) => {
      const mats = await this.requestV2Service.getTopAlternativeMaterial(id);
      for (const m in mats.items) {
        const mat = mats.items[m];
        mat.version.version_images = mat.version.version_images.filter(i => i.id_image);
        mat.version.version_images_urls = null;
        mat.mainImage = null;
        mat.version.unitType = this.getUnitType(mat.version);
        if (mat.version.version_images.length > 1) {
          mat.version.version_images_urls = mat.version.version_images.map((i) => {
            return {
              url : `${environment.api_url}/images/${i.id_image}?size=300x300`
            };
          });
        }
      }
      return {
        id_category: id,
        materials: mats.items.filter((mat) => {
          if (mat.version.version_images.length > 0) {
            return true;
          }
        })
      };
    }));
  }

  public async getProjectRoomsWithAlternativeMats() {
    this.topAlternative = await this.fetchTopAlternatives();

    return this.projectRooms.map((room) => {
      const r = { ...room };
      r.materials.map((mat) => {
        const m = { ...mat };
        const findAlternatives = this.topAlternative.find(c => c.id_category === mat.id_category);
        m.alternativeMat.materials = (findAlternatives ? findAlternatives.materials : []);
        m.alternativeMat.isLoaded = true;
        m.alternativeMat.indexSelectedFirst = (m.alternative
          && m.alternativeMat.materials.length > 0)
          ? this.randomIntFromInterval(0, m.alternativeMat.materials.length - 1, m, 'first')
          : 0;
        m.alternativeMat.indexSelectedSecond =
          (m.alternativeMat.materials.length > 0 && m.alternativeMat.materials.length !== 1)
            ? this.randomIntFromInterval(0, m.alternativeMat.materials.length - 1, m, 'second')
            :  m.alternativeMat.indexSelectedFirst;
        return m;
      });
      return r;
    });
  }

  public calcPercentageValidated() {
    return Math.round(((this.quotationMaterialsValidation.length * 100)
      / this.quotationMaterials.length));
  }

  async initChaptersWithMaterials(formattedMaterials: Material[]): Promise<void> {
    const chapters = [];
    formattedMaterials.forEach((material: Material) => {
      const chapter = chapters.find(chapter => chapter.id === material.id_quotation_chapter);
      if (!chapter) {
        chapters.push({
          id: material.id_quotation_chapter,
          title: material.quotation_chapter,
          materials: [
            material
          ]
        });
      } else {
        chapter.materials = [...chapter.materials, material];
      }
    });
    this.chaptersWithMaterials = [...chapters];
  }

  public async computeQuotationMaterialsData(fullInit = true): Promise<void> {
    const quotationMaterials = await this.requestV2Service.getQuotationMaterials(
      null,
      this.currentProject.id,
      true,
      'all',
      'false'
    );

    // Build Array Of Ids from quotation Materials related to "petites fournitures" categories in order to hide them
    const idsQuotMatToHide = quotationMaterials.items.filter((quotMat) => {
      if (quotMat._related.material_product_categories.length === 0) {
        return true;
      }
      return quotMat._related.material_product_categories[0].slug === 'petites-fournitures';
    }).map(mat => mat._related.material_versions[0].id);

    quotationMaterials.items = quotationMaterials.items.filter((quotMat) => {
      if (quotMat._related.material_product_categories.length === 0) {
        return true;
      }
      return quotMat._related.material_product_categories[0].slug !== 'petites-fournitures';
    });

    // Fetch quotations Materials related to the project without format it.
    const quotationMaterialsValidation:any = await this.requestV2Service
      .getListQuotationMaterialValidation(this.currentProject.id, 'all');

    // Filter quotation material validations.
    // Hide materials related to "petites fournitures" category
    this.quotationMaterialsValidation = quotationMaterialsValidation.items
      .filter(quotMat => idsQuotMatToHide.indexOf(quotMat.id_material_version) === -1);

    this.quotationMaterials = await this.formatMaterials(quotationMaterials.items);

    this.percentageValidated = this.calcPercentageValidated();

    if (!fullInit) {
      return;
    }

    // Fetch unit Type
    const unitsResults = await this.requestV2Service.getUnitTypes();
    this.units = unitsResults.items;

    // Fetch Project Rooms
    const projectRooms = await this.requestV2Service.getProjectRooms(this.currentProject);
    this.projectRooms = await this.formatProjectRooms(projectRooms.items);

    this.projectRooms = await this.formatFullProjectRoomsWithMat();

    this.projectRooms = await this.sortMatByStatus();

    await this.initChaptersWithMaterials(this.quotationMaterials);

    this.idsCategories = this.buildIdsCategories();

    // Add Alternative in async. Load first all material even if alternatives are not loaded
    this.getProjectRoomsWithAlternativeMats().then(
      (updatedProjectRooms) => {
        this.projectRooms = updatedProjectRooms;
      }
    );
  }

  public async computeProjectMaterialsData() {
    this.projectMaterials.items = [];

    if (this.currentProject) {
      const projectMaterials = await this.requestV2Service
        .getProjectMaterials(
          this.currentProject.id,
          true,
          'all');

      if (projectMaterials && projectMaterials.items.length > 0) {
        this.projectMaterials.items = projectMaterials.items.filter(pm => pm.state !== 'cancelled');
      } else {
        this.projectMaterials.items = [];
      }
    }
  }

  public getProjectMaterials() {
    return this.projectMaterials;
  }

  public hasProjectMaterials() {
    return this.projectMaterials.items.length > 0;
  }
}
